import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { DateFilterFn, MatCalendar, MatCalendarCellClassFunction } from '@angular/material/datepicker';
import { ICalendarAvailability } from '@core/availability/calendar-availability.model';
import { ComponentBase } from '@core/base/base.component.base';
import { IDictionary } from '@core/common.model';
import { DateFormatService } from '@core/date/date-format.service';
import { takeUntil, tap } from 'rxjs/operators';
import { VenueConfigService } from '../venue-config/venue-config.service';
import { CalendarHeaderComponent } from './calendar-header/calendar-header.component';

@Component({
  selector: 'app-calendar-availability',
  templateUrl: './calendar-availability.component.html',
  styleUrls: ['./calendar-availability.component.scss']
})
export class CalendarAvailabilityComponent extends ComponentBase implements AfterViewInit, OnChanges {
  @Input() currentDate = new Date();
  @Input() availabilityData!: ICalendarAvailability[];
  @Input() isDataVisible = false;

  @Output() dateSelected: EventEmitter<Date> = new EventEmitter<Date>();
  @Output() calendarActiveDate: EventEmitter<Date> = new EventEmitter<Date>();

  availabilityHeader = CalendarHeaderComponent;
  startDate = new Date();
  customCssClasses: IDictionary<string> = {
    available: 'custom-date-class-green',
    filling: 'custom-date-class-amber',
    soldOut: 'custom-date-class-sold-out',
    onlineSalesClosed: 'custom-date-class-online-closed',
    notAvailable: ''
  };

  @ViewChild('availabilityCalendar', { static: false }) availabilityCalendar!: MatCalendar<Date>;

  constructor(
    private dateFormatService: DateFormatService,
    private dateAdapter: DateAdapter<Date>,
    private venueConfigService: VenueConfigService
  ) {
    super();
    this.dateAdapter.setLocale(this.dateFormatService.venueLocale);
    const venueStartDate = this.venueConfigService.getVenueConfig().dateSettings.date;
    if (venueStartDate && this.dateFormatService.isDayBeforeFirstDate(venueStartDate, this.startDate)) {
      this.startDate = this.dateFormatService.dateOnlyStringToDate(venueStartDate);
    }
  }

  ngAfterViewInit(): void {
    this.availabilityCalendar.stateChanges
      .pipe(
        tap(() => this.calendarActiveDate.emit(this.availabilityCalendar.activeDate)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe();
  }

  ngOnChanges({ availabilityData }: SimpleChanges): void {
    if (!!availabilityData && !availabilityData.firstChange) {
      this.availabilityCalendar.updateTodaysDate();
    }
  }

  /** Sets any dates that are not-available to disabled state. */
  dateFilter: DateFilterFn<Date> = (cellDate: Date | null): boolean => {
    if (!cellDate) {
      return false;
    }

    const date = this.dateFormatService.dateToStringDateOnlyHyphen(cellDate);
    const findAvailable = this.availabilityData.find(
      (dayAvailable: ICalendarAvailability) => dayAvailable.date === date
    );
    if (!findAvailable) {
      return false;
    }

    return findAvailable.availability !== 'notAvailable';
  };

  /**
   * Function triggered on rendering of the mat-calendar. Applies additional classes to the mat-calendar-body-cell.
   * @param cellDate date object to evaluate.
   * @param view current view of the calendar (only interested in month).
   * @returns css class name as string.
   */
  dateClass: MatCalendarCellClassFunction<Date> = (cellDate: Date, view: string): string => {
    if (view === 'month' && this.availabilityData.length > 0 && this.isDataVisible) {
      const date = this.dateFormatService.dateToStringDateOnlyHyphen(cellDate);
      const findAvailable = this.availabilityData.find(
        (dayAvailable: ICalendarAvailability) => dayAvailable.date === date
      );

      if (this.dateFormatService.isDayBeforeFirstDate(this.startDate, cellDate) || !findAvailable) {
        return '';
      }

      // eslint-disable-next-line no-prototype-builtins
      if (this.customCssClasses.hasOwnProperty(findAvailable.availability)) {
        return this.customCssClasses[findAvailable.availability];
      }
    }

    return '';
  };

  /** Emits any date selected events. */
  onDateSelected(ev: Date | null): void {
    if (!ev) {
      return;
    }

    this.dateSelected.emit(ev);
  }
}
